/**
 * KAN-815: text of the next bot touch in Moscow time.
 * Full line: "Бот напишет 15.10 в 17:26 · шаг 1 из 3 · <summary>";
 * a manager task step (sent_by_bot false) starts with "Задача менеджеру";
 * short form for the CRM row: "15.10 17:26".
 */
import { moscowParts } from '@/lib/crmBoard';
import type {
  NextBotTouch,
  NextBotTouchDraft,
  NextBotTouchDraftKind,
  SendNextBotTouchStatus,
} from '@/types/nextBotTouch';

const pad = (n: number) => String(n).padStart(2, '0');

/** "15.10" and "17:26" of an ISO time in Moscow; a broken date gives null. */
function moscowDayAndClock(iso: string): { day: string; clock: string } | null {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return null;
  const p = moscowParts(date);
  return { day: `${pad(p.day)}.${pad(p.month)}`, clock: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** "шаг 1 из 3", or "шаг 2" when the ladder length is unknown. */
function stepLabel(touch: NextBotTouch): string {
  return touch.steps_total ? `шаг ${touch.step} из ${touch.steps_total}` : `шаг ${touch.step}`;
}

export function formatNextBotTouchLine(touch: NextBotTouch): string {
  const when = moscowDayAndClock(touch.at);
  const lead = touch.sent_by_bot ? 'Бот напишет' : 'Задача менеджеру';
  const head = when ? `${lead} ${when.day} в ${when.clock}` : lead;
  return [head, stepLabel(touch), touch.summary.trim()].filter(Boolean).join(' · ');
}

export function formatNextBotTouchShort(touch: NextBotTouch): string {
  const when = moscowDayAndClock(touch.at);
  return when ? `${when.day} ${when.clock}` : '';
}

/** Short result line of send-now by the backend status. */
export const SEND_NOW_RESULT_TEXT: Record<SendNextBotTouchStatus, string> = {
  sent: 'Отправлено',
  skipped: 'Бот решил не писать: напоминания по этой переписке закрыты',
  later: 'Шаг пропущен, следующий по расписанию',
  task: 'Создана задача менеджеру',
};

/** Toast style of the send-now result: success when something happened, info otherwise. */
export const SEND_NOW_RESULT_KIND: Record<SendNextBotTouchStatus, 'success' | 'info'> = {
  sent: 'success',
  skipped: 'info',
  later: 'info',
  task: 'success',
};

/** KAN-817: line shown instead of the text for a draft without text. */
export const DRAFT_NO_TEXT_LINE: Record<Exclude<NextBotTouchDraftKind, 'text'>, string> = {
  skip: 'Бот решил не писать',
  later: 'Бот пропустит этот шаг',
};

/** KAN-817: what the draft says: its text, or the skip / later line. */
export function formatNextBotTouchDraft(draft: NextBotTouchDraft): string {
  return draft.kind === 'text' ? draft.text : DRAFT_NO_TEXT_LINE[draft.kind];
}

/**
 * KAN-817: draft to show. Right after the draft action the touch prop still
 * lags (the refresh is running), so the response draft of the same job wins.
 * Once the prop was refreshed it is trusted: a prop without a draft or with a
 * draft not older than the response wins (the server no longer has it, or has
 * the same one); only an older prop draft (a stale refetch) loses.
 */
export function pickNextBotTouchDraft(
  touch: NextBotTouch,
  fresh: NextBotTouch | null | undefined,
  refreshed: boolean
): NextBotTouchDraft | null {
  const own = touch.draft ?? null;
  const latest = fresh && fresh.job_id === touch.job_id ? fresh.draft ?? null : null;
  if (!latest) return own;
  if (!refreshed) return latest;
  if (!own || Date.parse(own.created_at) >= Date.parse(latest.created_at)) return own;
  return latest;
}

/** KAN-817: line of a draft older than 24 h. */
export const DRAFT_EXPIRED_LINE = 'Черновик устарел, бот перепишет при отправке';

/** KAN-817: "черновик от 15.10 16:00" (Moscow); empty for a broken date. */
export function formatNextBotTouchDraftTime(draft: NextBotTouchDraft): string {
  const when = moscowDayAndClock(draft.created_at);
  return when ? `черновик от ${when.day} ${when.clock}` : '';
}

/** KAN-817: title of the compact note: the line and, when present, the draft. */
export function formatNextBotTouchTitle(touch: NextBotTouch): string {
  const line = formatNextBotTouchLine(touch);
  if (!touch.draft) return line;
  const parts = [formatNextBotTouchDraft(touch.draft).trim()];
  if (touch.draft.expired) parts.push(DRAFT_EXPIRED_LINE);
  const draft = parts.filter(Boolean).join('\n');
  return draft ? `${line}\n\n${draft}` : line;
}
